/**
 * Custom customer-receipt designer — shared model + renderer.
 *
 * Both the admin designer (live preview) and the cashier print path import
 * from here so "what you design" === "what prints". A design is a page
 * config + an ordered list of styled blocks. Each block binds order-detail
 * fields via {{token}} placeholders.
 *
 * The renderer is pure (no DOM / no React) and emits a full standalone HTML
 * document — the same shape the cashier already writes into its print iframe.
 * It consumes a flat `ReceiptRenderModel` (pre-formatted strings) so the
 * admin can feed sample data and the cashier can feed a real order through
 * the identical code path.
 *
 * Keep the customer receipt only — kitchen tickets are a separate ESC/POS
 * path and are intentionally NOT designed here.
 */

import type { ReceiptPrintOptions } from './print';
import { code39Svg } from './barcode';

// ── Design model ───────────────────────────────────────────────────────────

export type ReceiptAlign = 'left' | 'center' | 'right';
export type ReceiptLineStyle = 'solid' | 'dashed' | 'dotted' | 'double';

export interface ReceiptBlockStyle {
  fontFamily?: string;
  fontSizePx?: number;
  bold?: boolean;
  italic?: boolean;
  align?: ReceiptAlign;
  color?: string;
  marginTopPx?: number;
  marginBottomPx?: number;
  letterSpacingPx?: number;
  uppercase?: boolean;
  underline?: boolean;
  /**
   * Take the block out of the flow and push it to one side, so the blocks
   * AFTER it wrap alongside instead of below.
   *
   * This exists for one shape the block flow could not express: a barcode (or
   * a logo) sitting in the top-right corner with the invoice's own metadata
   * running down its left — which is what nojoom_live's slip does, and what
   * the legacy Sismatix ticket did before it. Everything keeps flowing past a
   * float until something clears it, so pair it with `clearFloat` on the first
   * block that must start below — the renderer also clears at every divider
   * and at the items table, which covers the ordinary case by itself.
   */
  float?: 'left' | 'right';
  /** Start below any floated block instead of wrapping beside it. */
  clearFloat?: boolean;
  /** Width, as a percentage of the printable band. Mainly for floated blocks. */
  widthPct?: number;
}

interface BlockBase {
  id: string;
  style?: ReceiptBlockStyle;
  /**
   * Optional token name; when set and that token resolves to an empty value
   * the whole block is skipped. Lets a design hide e.g. the Customer or Tax
   * line when the order has no customer / the tenant has no tax number.
   */
  hideIfEmpty?: string;
}

/** Free text with {{token}} placeholders (e.g. "Order #{{order_no}}"). */
export interface TextBlock extends BlockBase {
  type: 'text';
  text: string;
  /**
   * What this block says on an Arabic receipt. Optional: leave it unset and
   * the renderer translates the English by itself (see `arabicTemplate`) —
   * this is only for a word the built-in dictionary does not know, or one the
   * tenant wants said differently.
   */
  textAr?: string;
}

/** A justified left↔right line — both sides are {{token}} templates. */
export interface RowBlock extends BlockBase {
  type: 'row';
  left: string;
  right: string;
  /** Arabic override for `left` — see `TextBlock.textAr`. */
  leftAr?: string;
  /** Arabic override for `right`. Rarely needed: it is usually a token. */
  rightAr?: string;
}

/** The tenant's receipt logo (from Branding). Auto-skips when none uploaded. */
export interface LogoBlock extends BlockBase {
  type: 'logo';
  widthPct?: number;
}

/** A custom image embedded in the design itself (base64 data-URL). */
export interface ImageBlock extends BlockBase {
  type: 'image';
  dataUrl: string;
  widthPct?: number;
}

export interface DividerBlock extends BlockBase {
  type: 'divider';
  lineStyle?: ReceiptLineStyle;
}

/**
 * A scannable barcode of any {{token}} template — the invoice number, in
 * practice.
 *
 * Drawn as inline SVG by the shared renderer (`code39Svg`), so the admin
 * preview and the printed slip are the same picture and neither needs a
 * barcode library. Skipped silently when the template resolves to nothing
 * scannable, which is what makes it safe to leave on a design whose receipts
 * do not all carry the bound field.
 */
export interface BarcodeBlock extends BlockBase {
  type: 'barcode';
  /** {{token}} template — e.g. "{{order_no}}". */
  value: string;
  /**
   * Only Code 39 today: it is what the legacy slip printed and what the
   * tenants' scanners are set to read. The field exists so adding Code 128
   * later does not have to rewrite every saved design.
   */
  symbology?: 'code39';
  /** Bar height in px (the caption, when shown, adds to this). */
  heightPx?: number;
  /** Width of one narrow bar in px. Raise it if the scanner struggles. */
  moduleWidthPx?: number;
  /** Print the value in digits beneath the bars. Default true. */
  showValue?: boolean;
}

export interface SpacerBlock extends BlockBase {
  type: 'spacer';
  heightPx?: number;
}

/**
 * The Saudi (ZATCA) tax QR. Drawn only on a tenant set up for ZATCA — a VAT
 * number on the company and a SAR currency — and skipped silently everywhere
 * else, so a design can carry it whatever the tenant's country. The QR itself
 * is built by the caller (`model.zatcaQrSvg`): the payload rules live with the
 * receipt package, which also prints it on the built-in slip.
 */
export interface ZatcaQrBlock extends BlockBase {
  type: 'zatca_qr';
  /** Printed size of the square, mm. Default 30. */
  sizeMm?: number;
}

export type ItemsField = 'name' | 'qty' | 'price' | 'discount' | 'total';

export interface ItemsColumn {
  field: ItemsField;
  label?: string;
  /**
   * Header on an Arabic receipt. Unset is the normal case — the field is a
   * known one, so the renderer already knows its Arabic word.
   */
  labelAr?: string;
  align?: ReceiptAlign;
  widthPct?: number;
  /**
   * On a `qty` column, print the unit after the number — "3 Meal" rather than
   * "3". Off by default so every design saved before this existed prints the
   * column exactly as it did; the unit is only ever named on a multi-unit
   * item, so even with it on a single-unit catalogue reads unchanged.
   */
  withUnit?: boolean;
}

export interface ItemsBlock extends BlockBase {
  type: 'items';
  columns: ItemsColumn[];
  showHeader?: boolean;
  /**
   * Which rules the header row carries. Default 'bottom' — what the block has
   * always drawn. 'both' boxes the header between two lines, the way the
   * legacy ticket's ITEM / QTY / AMOUNT bar reads.
   */
  headerRule?: 'bottom' | 'both' | 'none';
  /**
   * Print the item's Arabic name under its English one.
   *
   * LEAVE IT UNSET to follow the tenant's own "Item name in Arabic & English"
   * print option (`ShowItemNameInReceiptEnglishAndArabic`), which is what the
   * Receipt Designer's "Print options" card says it does. An explicit `true`
   * or `false` is a per-design OVERRIDE and the tenant's checkbox then cannot
   * reach this block — GL_WEB_nojoom_live's seeded slip carried `false` and
   * ticking the option changed nothing on the paper.
   */
  showArabicName?: boolean;
  /**
   * Print the item's ARABIC name alone, without the English one (the user,
   * 2026-09-28 — some customers want only Arabic on the slip). Falls back to
   * the English name for an item that has no Arabic name, so no line prints
   * blank. Overrides `showArabicName` when set; unset on every design saved
   * before it existed, so they print exactly as before.
   */
  arabicNameOnly?: boolean;
  /**
   * Print the line's modifiers / its note. LEAVE UNSET to follow the tenant's
   * "Print item notes & options" option (`IsPrintCustomOptions`) — the same
   * rule, and for the same reason, as `showArabicName` above.
   */
  showModifiers?: boolean;
  showNote?: boolean;
  /** Print each item's code (item number) as a sub-line. */
  showItemCode?: boolean;
  /** Print each item's barcode as a sub-line. */
  showBarcode?: boolean;
}

export type TotalsKey =
  | 'subtotal' | 'discount' | 'delivery' | 'service' | 'tax'
  | 'total' | 'paid' | 'change';

export interface TotalsRow {
  key: TotalsKey;
  label?: string;
  /**
   * The row's label on an Arabic receipt. Unset is the normal case — the key
   * is a known one, so the renderer already knows its Arabic word.
   */
  labelAr?: string;
  bold?: boolean;
  fontSizePx?: number;
  /**
   * Literal text in front of the amount — "+ " on a delivery fee, so the
   * customer reads the line as an addition to the total above it rather than
   * a second total. Not part of the value, so it never reaches any sum.
   */
  valuePrefix?: string;
}

export interface TotalsBlock extends BlockBase {
  type: 'totals';
  rows: TotalsRow[];
  /** Skip rows whose value is zero/absent. Default true. */
  hideZero?: boolean;
}

export interface PaymentsBlock extends BlockBase {
  type: 'payments';
  showChange?: boolean;
}

export type ReceiptBlock =
  | TextBlock | RowBlock | LogoBlock | ImageBlock | DividerBlock
  | SpacerBlock | ItemsBlock | TotalsBlock | PaymentsBlock | BarcodeBlock
  | ZatcaQrBlock;

export type ReceiptBlockType = ReceiptBlock['type'];

export interface ReceiptPage {
  /** Paper width in millimetres (58 or 80 typically). */
  widthMm: number;
  /**
   * The width the print HEAD can actually image, in millimetres — NOT the
   * paper width. A "80 mm" head burns 576 dots at 203 dpi = 72 mm; a "58 mm"
   * head burns 384 = 48 mm. Anything laid out beyond it is silently clipped by
   * the printer, never scaled.
   *
   * The rendered page is sized to THIS, so nothing can land outside the band
   * no matter what the driver does with the leftover paper. Leave it unset to
   * take the standard for the paper width; set it when a particular printer
   * images less (some rolls are 76 mm, some drivers reserve an extra margin)
   * and the right-hand column comes out chopped.
   */
  printWidthMm?: number;
  marginMm?: number;
  fontFamily?: string;
  baseFontPx?: number;
  /** 'auto' uses the model's resolved direction (RTL when Arabic-first). */
  direction?: 'ltr' | 'rtl' | 'auto';
  color?: string;
  /**
   * Print the currency code after every amount ("13.500 KWD"). Default true —
   * what every design saved before this switch existed does.
   *
   * A single-currency shop naming its currency on every line is noise, and the
   * legacy slips these designs reproduce print bare numbers. It is a PAGE
   * choice, not a per-row one, so a receipt can never come out half-labelled.
   */
  showCurrencyCode?: boolean;
  /**
   * Let the SAME slip fill whatever paper it is sent to — the roll behind the
   * counter and an A4 sheet in the office, with no second design, no setting
   * and nothing for the cashier to choose.
   *
   * It works by giving up the one thing that makes the ordinary path safe:
   * `@page { size: <band>mm auto }`. That forced size is what stops a driver
   * clipping the right-hand column (see `printWidthMm`), and it is also why an
   * A4 printer receives a 72 mm ribbon down the left of the sheet — the page
   * IS 72 mm, whatever the paper is. With `size: auto` the printer's own paper
   * decides, and a print-width media query picks the layout: narrow paper
   * keeps today's geometry exactly, wide paper scales the whole design up to
   * fill the sheet.
   *
   * On wide paper it is also ONE page with the paper filled, whatever the
   * order holds. The sheet measures itself and lays a long slip out on a WIDER
   * band — fewer wrapped lines, so a longer order still fits the height at the
   * zoom that fills the width — keeping the items in one column under each
   * other. See the fitting script in `renderReceiptDesignHtml`; a roll never
   * matches any of it.
   *
   * **Off by default, and it must stay that way.** Turning it on re-exposes
   * the clipping the forced page size was introduced to fix, and whether a
   * given roll clips is a property of its driver — it can only be settled by a
   * test print on the actual printer. Enable it per design, per tenant, after
   * that print, never fleet-wide.
   */
  fitPaper?: boolean;
}

export interface ReceiptDesign {
  version: 1;
  page: ReceiptPage;
  blocks: ReceiptBlock[];
}

/** Per-tenant config carried on PrintConfig. */
export interface ReceiptDesignConfig {
  enabled: boolean;
  design: ReceiptDesign | null;
}

// ── Render model (what the renderer consumes) ───────────────────────────────

export interface ReceiptRenderItem {
  name: string;
  nameAr?: string | null;
  /** Item code / item number (pre-resolved). */
  code?: string | null;
  /** Item barcode (pre-resolved). */
  barcode?: string | null;
  qty: string;
  /**
   * The unit the line sold in, already resolved to the slip's language
   * ("Meal", "CTN"). Named only on a multi-unit item, and printed only by a
   * `qty` column with `withUnit` on.
   */
  unit?: string | null;
  price: string;
  discount?: string | null;
  total: string;
  mods?: string[];
  note?: string | null;
  /**
   * True when this line is a RETURNED item on a return/exchange receipt.
   * Together with `ReceiptRenderModel.exchange` it makes the items block print
   * an "Invoice" section and a "Refund" section instead of one flat list.
   */
  isReturn?: boolean;
}

/**
 * Return/exchange summary — present only on a receipt that carries return
 * lines. All money values are pre-formatted strings, like the rest of the
 * render model.
 *
 * When set, the items block groups its lines into Invoice (sold) + Refund
 * (returned) sections and the totals block prints the two legs plus the net,
 * mirroring the built-in receipt template. Without it a custom design printed
 * sold and returned items in one undifferentiated list.
 */
export interface ReceiptRenderExchange {
  /** Entry number of the sale (Code-51) leg — printed on the Invoice heading. */
  saleEntryNo?: string | null;
  /** Entry number of the return (Code-53) leg — printed on the Refund heading. */
  returnEntryNo?: string | null;
  /** Sold leg total. Empty/absent hides the row. */
  soldTotal?: string | null;
  /** Returned leg total, positive. Empty/absent hides the row. */
  returnedTotal?: string | null;
  /** 'NET TO COLLECT' / 'NET TO REFUND' — resolved by the caller. */
  netLabel: string;
  /** Absolute net difference, pre-formatted. */
  netValue: string;
}

export interface ReceiptRenderPayment {
  label: string;
  amount: string;
}

export interface ReceiptRenderModel {
  /** token -> already-formatted, human-readable string. */
  fields: Record<string, string>;
  items: ReceiptRenderItem[];
  /** totals key -> formatted value. Absent keys are treated as empty. */
  totals: Partial<Record<TotalsKey, string>>;
  payments: ReceiptRenderPayment[];
  /**
   * Automatic promotions this receipt matched, NAMED — the label already in
   * the slip's language, the amount already formatted. Their money is part of
   * the discount: when this is set, `totals.discount` carries only what is
   * left UNNAMED, and the totals block prints these rows where the design put
   * its Discount row (or above TOTAL when it has none). The same split the
   * built-in slip prints, so every figure on the paper counts once. Absent on
   * a receipt without promotions — a design prints exactly what it did.
   */
  promotions?: Array<{ label: string; amount: string }>;
  /**
   * Set on a return/exchange receipt. Drives the grouped Invoice/Refund items
   * layout and the Sold/Returned/NET totals — the same split the built-in
   * receipt prints.
   */
  exchange?: ReceiptRenderExchange | null;
  changeValue?: string | null;
  logoDataUrl?: string | null;
  /**
   * The ZATCA QR as an inline <svg>, or null/absent when the tenant is not set
   * up for ZATCA. Only the `zatca_qr` block reads it.
   */
  zatcaQrSvg?: string | null;
  /** Trusted tenant footer HTML appended verbatim after all blocks. */
  footerHtml?: string | null;
  direction: 'ltr' | 'rtl';
  /**
   * Print this design's LABELS in Arabic — the tenant's "Print receipt in
   * Arabic" option (`ArabiReset`), the same flag that sets `direction`.
   *
   * A design's labels are words the tenant typed, so there is no label key to
   * look up the way the built-in receipt does. The renderer translates them
   * instead: an explicit `*Ar` override wins, then the known key (a totals
   * key, an items field), then a dictionary of the words a receipt actually
   * uses. Anything the dictionary does not know prints exactly as typed —
   * which is why a design in Arabic already, or one using a tenant's own
   * wording, is untouched by this.
   *
   * Absent on a model built by an older caller — treated as false, which is
   * what every design printed before this existed.
   */
  arabicLabels?: boolean;
  /**
   * Global receipt print options. When an ItemsBlock leaves a sub-line toggle
   * (barcode / item code / note / modifiers / Arabic name) undefined, the
   * renderer falls back to these tenant-wide flags — so the legacy
   * `mstr_ConfigurationParameter` settings drive the custom design too.
   * `showPriceWithTax` adds the inclusive-of-tax note under the totals.
   * `printUnitPrice` does NOT reach a design: its Price column is drawn.
   */
  printOptions?: ReceiptPrintOptions | null;
}

// ── Field token catalogue (drives the admin "insert field" menu) ────────────

export interface ReceiptTokenDef {
  token: string;
  labelEn: string;
  labelAr: string;
  /** Example value used in the admin live preview. */
  sample: string;
}

export const RECEIPT_FIELD_TOKENS: readonly ReceiptTokenDef[] = [
  { token: 'order_no', labelEn: 'Order number', labelAr: 'رقم الطلب', sample: '470' },
  { token: 'order_date', labelEn: 'Date', labelAr: 'التاريخ', sample: '2026-06-07' },
  { token: 'order_time', labelEn: 'Time', labelAr: 'الوقت', sample: '14:22' },
  // The two above are formatted by the till's own OS locale; these three are
  // formatted by us, so every printer in the fleet emits the same characters.
  { token: 'order_date_dmy', labelEn: 'Date (day first)', labelAr: 'التاريخ (يوم أولاً)', sample: '07-06-2026' },
  { token: 'order_date_ymd', labelEn: 'Date (year first)', labelAr: 'التاريخ (سنة أولاً)', sample: '2026-06-07' },
  { token: 'order_time_full', labelEn: 'Time with seconds', labelAr: 'الوقت بالثواني', sample: '02:22:38 pm' },
  { token: 'datetime', labelEn: 'Date & time', labelAr: 'التاريخ والوقت', sample: '2026-06-07 14:22' },
  { token: 'order_type', labelEn: 'Order type', labelAr: 'نوع الطلب', sample: 'Delivery' },
  { token: 'order_type_ar', labelEn: 'Order type (Arabic)', labelAr: 'نوع الطلب (عربي)', sample: 'توصيل' },
  // `Inv_Receipt_h.PrintCount` — how many times the receipt has been issued:
  // 1 as sold, one higher per edit. The legacy Sismatix slip prints the bare
  // number in its footer, which is what `print_count` reproduces. The second
  // token is empty until the receipt has actually been edited, so a design can
  // bind it with `hideIfEmpty` and mark only those.
  { token: 'print_count', labelEn: 'Print / issue no.', labelAr: 'رقم الإصدار', sample: '1' },
  {
    token: 'print_count_edited',
    labelEn: 'Issue no. (edited receipts only)',
    labelAr: 'رقم الإصدار (للفواتير المعدلة فقط)',
    sample: '2',
  },
  // The LATEST edit of the invoice, from the change log: who saved it and
  // when. Both are empty on a receipt that was never edited, so bind them with
  // `hideIfEmpty` and the line appears only on an edited copy.
  { token: 'edited_by', labelEn: 'Edited by (user)', labelAr: 'عُدّلت بواسطة', sample: 'Sara' },
  { token: 'edited_at', labelEn: 'Edit date & time', labelAr: 'تاريخ ووقت التعديل', sample: '2026-10-17 09:14 PM' },
  { token: 'payment_type', labelEn: 'Payment type', labelAr: 'طريقة الدفع', sample: 'Cash' },
  { token: 'cashier_name', labelEn: 'Cashier', labelAr: 'الكاشير', sample: 'Ahmed' },
  { token: 'customer_name', labelEn: 'Customer name', labelAr: 'اسم العميل', sample: 'Sara Ali' },
  { token: 'customer_phone', labelEn: 'Customer phone', labelAr: 'هاتف العميل', sample: '0100 123 4567' },
  {
    token: 'customer_address',
    labelEn: 'Customer address',
    labelAr: 'عنوان العميل',
    sample: 'Riyadh · Al Olaya · Block 12 · St 5 · Bldg 3',
  },
  // The same address, field by field, for a design that wants to write its own
  // labels ("Block: 3 Street: 308 House: 4" — the legacy slip's wording). Pair
  // them with the [[optional segment]] syntax so an unfilled field takes its
  // label with it instead of leaving "Street:" hanging on the paper.
  { token: 'addr_city', labelEn: 'Address — city', labelAr: 'العنوان — المدينة', sample: 'Kuwait' },
  { token: 'addr_district', labelEn: 'Address — district', labelAr: 'العنوان — المنطقة', sample: 'Salmiya' },
  { token: 'addr_block', labelEn: 'Address — block', labelAr: 'العنوان — قطعة', sample: '3' },
  { token: 'addr_street', labelEn: 'Address — street', labelAr: 'العنوان — شارع', sample: '308' },
  { token: 'addr_avenue', labelEn: 'Address — avenue', labelAr: 'العنوان — جادة', sample: '4' },
  { token: 'addr_house', labelEn: 'Address — house / building', labelAr: 'العنوان — منزل', sample: '4' },
  { token: 'addr_floor', labelEn: 'Address — floor', labelAr: 'العنوان — طابق', sample: '2' },
  { token: 'addr_flat', labelEn: 'Address — flat', labelAr: 'العنوان — شقة', sample: '7' },
  { token: 'addr_note', labelEn: 'Address — landmark note', labelAr: 'العنوان — علامة مميزة', sample: 'beside the mosque' },
  { token: 'table', labelEn: 'Table label', labelAr: 'الطاولة', sample: 'Table: 7' },
  // The till's **Notes** box — the free text the cashier types in the payment
  // dialog. Empty on every sale nobody wrote a note on, so bind it with
  // `hideIfEmpty` and the line disappears on an ordinary receipt. The A4
  // designer carries the same token under the same name.
  { token: 'notes', labelEn: 'Notes (till)', labelAr: 'الملاحظات', sample: 'Delivered to the reception desk' },
  // Empty on every non-delivery order — bind it with `hideIfEmpty`.
  { token: 'delivery_datetime', labelEn: 'Delivery date & time', labelAr: 'تاريخ ووقت التوصيل', sample: '2026-06-07 07:30 PM' },
  { token: 'company_name', labelEn: 'Company name', labelAr: 'اسم الشركة', sample: 'Sahara Restaurant' },
  { token: 'company_name_ar', labelEn: 'Company name (Arabic)', labelAr: 'اسم الشركة (عربي)', sample: 'مطعم صحارى' },
  { token: 'branch_name', labelEn: 'Branch name', labelAr: 'اسم الفرع', sample: 'Main Branch' },
  { token: 'branch_name_en', labelEn: 'Branch name (English)', labelAr: 'اسم الفرع (إنجليزي)', sample: 'Main Branch' },
  { token: 'branch_name_ar', labelEn: 'Branch name (Arabic)', labelAr: 'اسم الفرع (عربي)', sample: 'الفرع الرئيسي' },
  // Whichever of the two `branch_name` did NOT print, and empty when the tenant
  // has only one name — the one token that can be paired with `branch_name` to
  // print the branch in both scripts without printing it twice.
  {
    token: 'branch_name_alt',
    labelEn: 'Branch name (other script)',
    labelAr: 'اسم الفرع (اللغة الأخرى)',
    sample: 'Main Branch',
  },
  { token: 'branch_address', labelEn: 'Branch address', labelAr: 'عنوان الفرع', sample: '12 King St / Cairo' },
  { token: 'company_address', labelEn: 'Company address', labelAr: 'عنوان الشركة', sample: '12 King St' },
  { token: 'company_contact', labelEn: 'Company contact', labelAr: 'تواصل الشركة', sample: '+20 2 1234 5678' },
  { token: 'tax_number', labelEn: 'Tax / VAT number', labelAr: 'الرقم الضريبي', sample: '310123456700003' },
  // Arabic in both languages: "فاتوره ضريبيه" when the customer has a VAT
  // number, else "فاتوره ضريبيه مبسطه" (UNIQUEVISION's wording). A reprint
  // carries no customer VAT number, so it always reads simplified.
  { token: 'tax_invoice_title_ar', labelEn: 'Tax invoice / simplified title (Arabic)', labelAr: 'فاتورة ضريبية / مبسطة', sample: 'فاتوره ضريبيه مبسطه' },
  { token: 'cr_number', labelEn: 'Commercial reg. no.', labelAr: 'السجل التجاري', sample: 'CR-99887' },
  { token: 'subtotal', labelEn: 'Subtotal', labelAr: 'الإجمالي الفرعي', sample: '180.00' },
  { token: 'discount', labelEn: 'Discount', labelAr: 'الخصم', sample: '0.00' },
  // The automatic promotions the sale matched, by name, joined with " + ".
  // Empty on a sale without one, so bind it with `hideIfEmpty`. The totals
  // block already prints each promotion with its amount; this is for a design
  // that wants the names somewhere else as well ("Offer: Morning mix").
  { token: 'promotion_names', labelEn: 'Promotion name(s)', labelAr: 'اسم العرض', sample: 'Morning mix' },
  { token: 'tax', labelEn: 'Tax', labelAr: 'الضريبة', sample: '27.00' },
  { token: 'total', labelEn: 'Total', labelAr: 'الإجمالي', sample: '207.00' },
  { token: 'paid', labelEn: 'Paid', labelAr: 'المدفوع', sample: '210.00' },
  { token: 'change', labelEn: 'Change', labelAr: 'الباقي', sample: '3.00' },
  { token: 'currency', labelEn: 'Currency', labelAr: 'العملة', sample: 'EGP' },
  // Return / exchange — empty on a plain sale, so a block bound to one of
  // these with `hideIfEmpty` only shows up on a return or exchange receipt.
  { token: 'sold_total', labelEn: 'Sold total (exchange)', labelAr: 'إجمالي المبيعات (استبدال)', sample: '120.00' },
  { token: 'returned_total', labelEn: 'Returned total (exchange)', labelAr: 'إجمالي المرتجع (استبدال)', sample: '45.00' },
  { token: 'net_total', labelEn: 'Net settled (exchange)', labelAr: 'صافي التسوية (استبدال)', sample: '75.00' },
  { token: 'net_label', labelEn: 'Net label (exchange)', labelAr: 'وصف الصافي (استبدال)', sample: 'NET TO COLLECT' },
];

const DEFAULT_TOTALS_LABELS: Record<TotalsKey, string> = {
  subtotal: 'Subtotal',
  discount: 'Discount',
  delivery: 'Delivery',
  service: 'Service',
  tax: 'Tax',
  total: 'TOTAL',
  paid: 'Paid',
  change: 'Change',
};

// ── Arabic labels for a custom design ───────────────────────────────────────

/**
 * The same words in Arabic, for a totals row whose key we know.
 *
 * These are the built-in receipt's own words (`receiptLabels()` in
 * `packages/receipt/src/buildReceiptHtml.ts`) so the two slips read alike —
 * `designArabicLabels.test.ts` fails if they drift apart.
 */
const DEFAULT_TOTALS_LABELS_AR: Record<TotalsKey, string> = {
  subtotal: 'الإجمالي الفرعي',
  discount: 'الخصم',
  delivery: 'رسوم التوصيل',
  service: 'الخدمة',
  tax: 'الضريبة',
  total: 'الإجمالي',
  paid: 'المدفوع',
  change: 'الباقي',
};

const DEFAULT_ITEM_LABELS: Record<ItemsField, string> = {
  name: 'Item', qty: 'Qty', price: 'Price', discount: 'Disc', total: 'Total',
};

const DEFAULT_ITEM_LABELS_AR: Record<ItemsField, string> = {
  name: 'الصنف', qty: 'الكمية', price: 'السعر', discount: 'الخصم', total: 'الإجمالي',
};

/**
 * Arabic for the words a receipt design actually puts on paper.
 *
 * This is what makes "Print receipt in Arabic" work on a CUSTOM design without
 * the tenant retyping their layout. It is deliberately a SMALL, CLOSED list of
 * receipt vocabulary, matched against a whole label and never a substring: a
 * word that is not in here prints exactly as typed. That is the safety
 * property — a tenant's own phrase, a brand name, a footer paragraph or a
 * design already written in Arabic all pass through untouched, and the failure
 * mode of an unknown word is the English it already printed.
 *
 * A key ending in `@token` is the more specific reading of an ambiguous word,
 * chosen when that token follows the label in the same block: "TAX:" in front
 * of `{{tax_number}}` is the seller's registration number (الرقم الضريبي),
 * while "Tax" on its own is the tax amount (الضريبة).
 */
const DESIGN_LABEL_AR: Record<string, string> = {
  // Document
  receipt: 'إيصال', invoice: 'فاتورة', bill: 'فاتورة', order: 'طلب',
  'order no': 'رقم الطلب', 'order number': 'رقم الطلب', 'order type': 'نوع الطلب',
  reprint: 'إعادة طباعة', copy: 'نسخة',
  // Header identity
  branch: 'الفرع', tel: 'هاتف', telephone: 'هاتف', fax: 'فاكس',
  tax: 'الضريبة', 'tax@tax_number': 'الرقم الضريبي',
  'tax no': 'الرقم الضريبي', 'tax number': 'الرقم الضريبي', vat: 'الرقم الضريبي',
  'vat no': 'الرقم الضريبي', 'vat number': 'الرقم الضريبي',
  cr: 'س.ت', 'cr no': 'س.ت', 'commercial register': 'السجل التجاري',
  // Who and when
  date: 'التاريخ', time: 'الوقت', 'date & time': 'التاريخ والوقت',
  'date and time': 'التاريخ والوقت', datetime: 'التاريخ والوقت',
  cashier: 'الكاشير', user: 'المستخدم', salesman: 'المندوب',
  customer: 'العميل', client: 'العميل', phone: 'الهاتف', mobile: 'الهاتف',
  address: 'العنوان', table: 'طاولة', 'delivery time': 'وقت التوصيل',
  // The {{edited_by}} / {{edited_at}} rows (who last edited the invoice, and when).
  'edited by': 'عُدّلت بواسطة', 'edit date': 'تاريخ التعديل', 'edited on': 'تاريخ التعديل',
  'edited at': 'تاريخ التعديل', 'edit time': 'وقت التعديل',
  'edit date & time': 'تاريخ ووقت التعديل', 'edit date and time': 'تاريخ ووقت التعديل',
  // Item grid
  item: 'الصنف', items: 'الأصناف', description: 'البيان',
  qty: 'الكمية', quantity: 'الكمية', unit: 'الوحدة',
  price: 'السعر', 'unit price': 'سعر الوحدة',
  disc: 'الخصم', amount: 'المبلغ', value: 'القيمة',
  // Money
  subtotal: 'الإجمالي الفرعي', 'sub total': 'الإجمالي الفرعي',
  discount: 'الخصم', delivery: 'رسوم التوصيل', 'delivery fee': 'رسوم التوصيل',
  service: 'الخدمة', 'service charge': 'رسوم الخدمة',
  total: 'الإجمالي', 'grand total': 'الإجمالي العام', net: 'الصافي',
  paid: 'المدفوع', payment: 'الدفع', change: 'الباقي',
  'cash tendered': 'المبلغ المدفوع', balance: 'الرصيد',
  'prices include tax': 'الأسعار شاملة الضريبة',
  // Closing
  note: 'ملاحظة', notes: 'ملاحظات',
  'thank you': 'شكراً لزيارتكم', 'thanks': 'شكراً لزيارتكم',
  welcome: 'أهلاً وسهلاً', 'see you soon': 'نتشرف بزيارتكم مرة أخرى',
};

/** A `{{token}}`, captured so a split keeps it. */
const TOKEN_SPLIT_RE = /(\{\{[^}]*\}\})/g;

function labelKey(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Translate ONE literal run of a template, keeping its spacing and its
 * trailing separator — "TAX: " stays "الرقم الضريبي: ", not "الرقم الضريبي".
 * `nextToken` is the token that immediately follows, when there is one.
 */
function arabicSegment(seg: string, nextToken: string | null): string {
  const m = /^(\s*)([\s\S]*?)(\s*)$/.exec(seg);
  if (!m) return seg;
  const [, lead = '', core = '', trail = ''] = m;
  if (!core) return seg;
  const sep = /[:：#!.\-–—]+$/.exec(core)?.[0] ?? '';
  const word = sep ? core.slice(0, core.length - sep.length).trim() : core;
  if (!word) return seg;
  const key = labelKey(word);
  const ar = (nextToken ? DESIGN_LABEL_AR[`${key}@${nextToken}`] : undefined) ?? DESIGN_LABEL_AR[key];
  return ar ? `${lead}${ar}${sep}${trail}` : seg;
}

/**
 * The Arabic reading of a `{{token}}` template: every literal word the
 * dictionary knows, translated; every token and every unknown word left
 * exactly as it is. Returns the original string when nothing matched, so an
 * already-Arabic design is provably unchanged.
 */
export function arabicTemplate(template: string): string {
  if (!template) return template;
  const parts = template.split(TOKEN_SPLIT_RE);
  let changed = false;
  const out = parts.map((part, i) => {
    if (!part || part.startsWith('{{')) return part;
    const next = parts[i + 1];
    const token = next && next.startsWith('{{') ? next.slice(2, -2).trim() : null;
    const t = arabicSegment(part, token);
    if (t !== part) changed = true;
    return t;
  });
  return changed ? out.join('') : template;
}

/**
 * A label for the slip's language: the tenant's own Arabic wording if they
 * gave one, otherwise the English run through the dictionary.
 */
function pickLabel(model: ReceiptRenderModel, en: string, ar: string | undefined): string {
  if (!model.arabicLabels) return en;
  return ar?.trim() ? ar : arabicTemplate(en);
}

// ── Default design (mirrors the built-in receipt; the "starting point") ─────

let _uid = 0;
function bid(prefix: string): string {
  _uid += 1;
  return `${prefix}_${_uid}`;
}

export function defaultReceiptDesign(): ReceiptDesign {
  return {
    version: 1,
    page: { widthMm: 80, marginMm: 3, baseFontPx: 12, direction: 'auto' },
    blocks: [
      { id: bid('b'), type: 'logo', widthPct: 55, style: { align: 'center', marginBottomPx: 4 } },
      { id: bid('b'), type: 'text', text: '{{company_name}}', style: { align: 'center', bold: true, fontSizePx: 16 } },
      { id: bid('b'), type: 'text', text: '{{company_name_ar}}', hideIfEmpty: 'company_name_ar', style: { align: 'center', bold: true, fontSizePx: 14 } },
      { id: bid('b'), type: 'text', text: '{{branch_name}}', hideIfEmpty: 'branch_name', style: { align: 'center', fontSizePx: 12 } },
      // The branch name in the other script, under the first line. Hidden on a
      // tenant that filled only one of the two columns.
      { id: bid('b'), type: 'text', text: '{{branch_name_alt}}', hideIfEmpty: 'branch_name_alt', style: { align: 'center', fontSizePx: 12 } },
      { id: bid('b'), type: 'text', text: '{{branch_address}}', hideIfEmpty: 'branch_address', style: { align: 'center', fontSizePx: 10, color: '#444' } },
      // The till's Notes box, under the address — the same place the built-in
      // slip puts it, so a tenant starting from this design keeps the receipt
      // they already had. Hidden on every sale without one.
      { id: bid('b'), type: 'text', text: '{{notes}}', hideIfEmpty: 'notes', style: { align: 'center', fontSizePx: 11 } },
      { id: bid('b'), type: 'text', text: 'TAX: {{tax_number}}', hideIfEmpty: 'tax_number', style: { align: 'center', fontSizePx: 10, color: '#444' } },
      { id: bid('b'), type: 'divider', lineStyle: 'dashed' },
      { id: bid('b'), type: 'row', left: 'Receipt', right: '#{{order_no}}', style: { bold: true } },
      { id: bid('b'), type: 'row', left: 'Date', right: '{{datetime}}', style: { fontSizePx: 11 } },
      { id: bid('b'), type: 'row', left: 'Cashier', right: '{{cashier_name}}', style: { fontSizePx: 11 } },
      { id: bid('b'), type: 'row', left: 'Customer', right: '{{customer_name}}', hideIfEmpty: 'customer_name', style: { fontSizePx: 11 } },
      { id: bid('b'), type: 'row', left: 'Table', right: '{{table}}', hideIfEmpty: 'table', style: { fontSizePx: 11, bold: true } },
      { id: bid('b'), type: 'divider', lineStyle: 'dashed' },
      {
        id: bid('b'), type: 'items',
        // `showArabicName`, `showModifiers` and `showNote` deliberately unset:
        // the starter design follows the tenant's print options ("Item name
        // in Arabic & English", "Print item notes & options") rather than
        // pinning an answer those checkboxes could not then change.
        showHeader: true,
        columns: [
          { field: 'name', label: 'Item', align: 'left' },
          { field: 'qty', label: 'Qty', align: 'center', widthPct: 14 },
          { field: 'price', label: 'Price', align: 'right', widthPct: 24 },
          { field: 'total', label: 'Total', align: 'right', widthPct: 26 },
        ],
        style: { fontSizePx: 11 },
      },
      { id: bid('b'), type: 'divider', lineStyle: 'dashed' },
      {
        id: bid('b'), type: 'totals', hideZero: true,
        rows: [
          { key: 'subtotal' },
          { key: 'discount' },
          { key: 'tax' },
          { key: 'total', bold: true, fontSizePx: 14 },
        ],
      },
      { id: bid('b'), type: 'divider', lineStyle: 'dashed' },
      { id: bid('b'), type: 'payments', showChange: true },
      { id: bid('b'), type: 'spacer', heightPx: 6 },
      // Prints only on a tenant set up for ZATCA (VAT number + SAR).
      { id: bid('b'), type: 'zatca_qr', sizeMm: 30, style: { align: 'center' } },
      { id: bid('b'), type: 'text', text: 'Thank you!', style: { align: 'center', fontSizePx: 12 } },
    ],
  };
}

// ── Renderer ────────────────────────────────────────────────────────────────

function esc(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Resolve every {{token}} in a template; unknown tokens → ''. */
function fillTokens(template: string, fields: Record<string, string>): string {
  return template.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (_m, name: string) => {
    const v = fields[name];
    return v == null ? '' : esc(v);
  });
}

/** True when a template piece resolves to nothing but whitespace. */
function segmentEmpty(template: string, fields: Record<string, string>): boolean {
  const tokens = template.match(/\{\{\s*[a-z0-9_]+\s*\}\}/gi);
  // A segment with no tokens at all is literal text — always keep it.
  if (!tokens || tokens.length === 0) return false;
  return tokens.every((t) => {
    const name = t.replace(/[{}\s]/g, '');
    const v = fields[name];
    return v == null || String(v).trim() === '';
  });
}

/**
 * Resolve a template's {{tokens}} and its [[optional segments]].
 *
 * `[[ … ]]` wraps a piece that carries its own wording — "[[Street: {{addr_street}} ]]".
 * The whole segment disappears when every token inside it is empty, so an
 * address missing its street prints "Block: 3 House: 4" rather than
 * "Block: 3 Street:  House: 4". `hideIfEmpty` could not do this: it hides the
 * entire block, and these labels share one line.
 *
 * Escaping happens before either substitution, so tenant-authored text and
 * resolved values are both inert HTML.
 */
function applyTokens(template: string, fields: Record<string, string>): string {
  const escaped = esc(template);
  const withSegments = escaped.replace(/\[\[([\s\S]*?)\]\]/g, (_m, inner: string) =>
    segmentEmpty(inner, fields) ? '' : inner,
  );
  return fillTokens(withSegments, fields);
}

/**
 * The same substitution with NO HTML escaping — for a value that is about to
 * be encoded rather than written into the document (the barcode block). Passing
 * it through `applyTokens` first would put `&amp;` into the bars.
 */
function applyTokensPlain(template: string, fields: Record<string, string>): string {
  const withSegments = String(template ?? '').replace(/\[\[([\s\S]*?)\]\]/g, (_m, inner: string) =>
    segmentEmpty(inner, fields) ? '' : inner,
  );
  return withSegments.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (_m, name: string) => fields[name] ?? '');
}

/** True when the named token (or, for blocks without a token, nothing) is empty. */
function tokenEmpty(token: string | undefined, fields: Record<string, string>): boolean {
  if (!token) return false;
  const v = fields[token];
  return v == null || String(v).trim() === '';
}

function styleCss(style: ReceiptBlockStyle | undefined): string {
  if (!style) return '';
  const out: string[] = [];
  if (style.fontFamily) out.push(`font-family:${style.fontFamily}`);
  if (style.fontSizePx) out.push(`font-size:${style.fontSizePx}px`);
  if (style.bold) out.push('font-weight:700');
  if (style.italic) out.push('font-style:italic');
  if (style.align) out.push(`text-align:${style.align}`);
  if (style.color) out.push(`color:${style.color}`);
  if (style.marginTopPx != null) out.push(`margin-top:${style.marginTopPx}px`);
  if (style.marginBottomPx != null) out.push(`margin-bottom:${style.marginBottomPx}px`);
  if (style.letterSpacingPx != null) out.push(`letter-spacing:${style.letterSpacingPx}px`);
  if (style.uppercase) out.push('text-transform:uppercase');
  if (style.underline) out.push('text-decoration:underline');
  if (style.float) out.push(`float:${style.float}`);
  if (style.clearFloat) out.push('clear:both');
  if (style.widthPct != null) out.push(`width:${Math.max(1, Math.min(100, style.widthPct))}%`);
  return out.join(';');
}

/**
 * The bits of a style a self-drawing block (logo, image, barcode) has to honour
 * itself, because it builds its own wrapper style rather than using `styleCss`.
 */
function boxCss(style: ReceiptBlockStyle | undefined, defaultAlign: ReceiptAlign): string {
  const out = [`text-align:${style?.align ?? defaultAlign}`];
  if (style?.marginTopPx != null) out.push(`margin-top:${style.marginTopPx}px`);
  if (style?.marginBottomPx != null) out.push(`margin-bottom:${style.marginBottomPx}px`);
  if (style?.float) out.push(`float:${style.float}`);
  if (style?.clearFloat) out.push('clear:both');
  if (style?.widthPct != null) out.push(`width:${Math.max(1, Math.min(100, style.widthPct))}%`);
  return out.join(';');
}

function renderBlock(block: ReceiptBlock, model: ReceiptRenderModel): string {
  if ('hideIfEmpty' in block && tokenEmpty(block.hideIfEmpty, model.fields)) return '';
  const css = styleCss(block.style);
  const styleAttr = css ? ` style="${css}"` : '';

  switch (block.type) {
    case 'text':
      return `<div class="rb-text"${styleAttr}>${applyTokens(pickLabel(model, block.text, block.textAr), model.fields)}</div>`;

    case 'row':
      return (
        `<div class="rb-row"${styleAttr}>` +
        `<span class="rb-row-l">${applyTokens(pickLabel(model, block.left, block.leftAr), model.fields)}</span>` +
        `<span class="rb-row-r">${applyTokens(pickLabel(model, block.right, block.rightAr), model.fields)}</span>` +
        `</div>`
      );

    case 'logo': {
      if (!model.logoDataUrl) return '';
      const w = Math.max(5, Math.min(100, block.widthPct ?? 55));
      return `<div class="rb-img" style="${boxCss(block.style, 'center')}"><img src="${esc(model.logoDataUrl)}" style="width:${w}%;max-height:34mm;object-fit:contain" alt="" /></div>`;
    }

    case 'image': {
      if (!block.dataUrl) return '';
      const w = Math.max(5, Math.min(100, block.widthPct ?? 100));
      return `<div class="rb-img" style="${boxCss(block.style, 'center')}"><img src="${esc(block.dataUrl)}" style="width:${w}%;object-fit:contain" alt="" /></div>`;
    }

    case 'barcode': {
      // The bound template is resolved, then reduced to what Code 39 can
      // actually carry. Nothing scannable left → no block at all, which is how
      // a design keeps a barcode bound to a field not every receipt has.
      const raw = applyTokensPlain(block.value ?? '', model.fields);
      const svg = code39Svg(raw, {
        heightPx: block.heightPx ?? 34,
        moduleWidthPx: block.moduleWidthPx ?? 1.4,
        showValue: block.showValue !== false,
        color: block.style?.color || 'currentColor',
      });
      if (!svg) return '';
      return `<div class="rb-barcode" style="${boxCss(block.style, 'center')}">${svg}</div>`;
    }

    case 'zatca_qr': {
      // Not set up for ZATCA → no block at all. The size is re-stamped onto
      // the caller's SVG so the design, not the caller, decides it.
      if (!model.zatcaQrSvg) return '';
      const s = Math.max(15, Math.min(60, block.sizeMm ?? 30));
      const svg = model.zatcaQrSvg.replace(/width="[^"]*" height="[^"]*"/, `width="${s}mm" height="${s}mm"`);
      return `<div class="rb-barcode" style="${boxCss(block.style, 'center')}">${svg}</div>`;
    }

    case 'divider': {
      const ls = block.lineStyle ?? 'dashed';
      // Dividers clear floats. A rule that runs THROUGH a floated barcode
      // rather than under it is never what the design meant, and it saves
      // every design from having to set `clearFloat` by hand.
      return `<div class="rb-divider" style="border-top:1px ${ls} currentColor;clear:both${css ? `;${css}` : ''}"></div>`;
    }

    case 'spacer':
      return `<div style="height:${Math.max(0, block.heightPx ?? 6)}px"></div>`;

    case 'items':
      return renderItems(block, model, styleAttr);

    case 'totals':
      return renderTotals(block, model, css);

    case 'payments':
      return renderPayments(block, model, css);

    default:
      return '';
  }
}

function renderItems(block: ItemsBlock, model: ReceiptRenderModel, styleAttr: string): string {
  // A Price column the tenant DREW prints whatever "Print unit price" says.
  // Tried the other way on 2026-09-23 and a fleet render showed six live
  // tenants (Abshar, AlSayersD, egydemo1, maraya, mfateel, tbloggers) with the
  // ERP flag off AND a Price column in their design — dropping it would have
  // changed their paper overnight. Drawing the column is the explicit choice.
  const cols = block.columns.length > 0
    ? block.columns
    : ([{ field: 'name', align: 'left' }, { field: 'qty', align: 'center' }, { field: 'total', align: 'right' }] as ItemsColumn[]);
  const ncols = cols.length;
  // A column's header: the tenant's Arabic wording, else their English one
  // translated, else the built-in word for that field in the slip's language.
  const headerFor = (c: ItemsColumn): string =>
    model.arabicLabels
      ? (c.labelAr?.trim()
        || (c.label ? arabicTemplate(c.label) : '')
        || DEFAULT_ITEM_LABELS_AR[c.field])
      : (c.label ?? DEFAULT_ITEM_LABELS[c.field]);

  const colgroup = cols
    .map((c) => `<col${c.widthPct ? ` style="width:${c.widthPct}%"` : ''} />`)
    .join('');

  const rule = block.headerRule ?? 'bottom';
  const headCss =
    rule === 'none' ? 'border-bottom:0'
    : rule === 'both' ? 'border-top:1px solid currentColor'
    : '';
  const head = block.showHeader
    ? `<thead><tr>${cols
        .map(
          (c) =>
            `<th style="text-align:${c.align ?? 'left'}${headCss ? `;${headCss}` : ''}">` +
            `${esc(headerFor(c))}</th>`,
        )
        .join('')}</tr></thead>`
    : '';

  const valueFor = (c: ItemsColumn, it: ReceiptRenderItem): string => {
    switch (c.field) {
      case 'name': {
        if (block.arabicNameOnly) {
          const only = it.nameAr?.trim() ? it.nameAr : it.name;
          return `<div class="rb-it-name" dir="ltr">${esc(only)}</div>`;
        }
        const bilingual = block.showArabicName ?? model.printOptions?.bilingualItemName ?? false;
        // `dir="ltr"` on BOTH lines so the Arabic name sits directly under the
        // English one at the same edge. Under `dir="rtl"` it drifted to the
        // opposite side of the column (and to the far side of the paper on an
        // Arabic receipt); the glyphs still shape RTL either way.
        const ar = bilingual && it.nameAr && it.nameAr !== it.name
          ? `<div class="rb-it-ar" dir="ltr">${esc(it.nameAr)}</div>` : '';
        return `<div class="rb-it-name" dir="ltr">${esc(it.name)}</div>${ar}`;
      }
      case 'qty': {
        // "3 Meal" — the unit only ever travels on a multi-unit item, so this
        // is "3" on an ordinary one whether the column asks for it or not.
        const unit = c.withUnit && it.unit ? ` ${it.unit}` : '';
        return `${esc(it.qty)}${esc(unit)}`;
      }
      case 'price': return esc(it.price);
      case 'discount': return it.discount ? esc(it.discount) : '';
      case 'total': return esc(it.total);
      default: return '';
    }
  };

  // Each sub-line toggle falls back to the tenant-wide print option when the
  // block leaves it undefined, so the legacy config flags drive the custom
  // design too. Modifiers and notes share the one `printCustomOptions` flag.
  const po = model.printOptions;
  const showItemCode = block.showItemCode ?? po?.showItemCode ?? false;
  const showBarcode = block.showBarcode ?? po?.showItemBarcode ?? false;
  const showModifiers = block.showModifiers ?? po?.printCustomOptions ?? false;
  const showNote = block.showNote ?? po?.printCustomOptions ?? false;

  const renderItemGroup = (it: ReceiptRenderItem): string => {
    const main = `<tr>${cols
      .map((c) => `<td class="rb-it-cell" style="text-align:${c.align ?? 'left'}">${valueFor(c, it)}</td>`)
      .join('')}</tr>`;
    const extras: string[] = [];
    if (showItemCode && it.code) {
      extras.push(`<tr><td class="rb-it-sub" colspan="${ncols}">#${esc(it.code)}</td></tr>`);
    }
    if (showBarcode && it.barcode) {
      extras.push(`<tr><td class="rb-it-sub" colspan="${ncols}">${esc(it.barcode)}</td></tr>`);
    }
    if (showModifiers && it.mods && it.mods.length > 0) {
      extras.push(`<tr><td class="rb-it-sub" colspan="${ncols}">${esc(it.mods.join(' · '))}</td></tr>`);
    }
    if (showNote && it.note) {
      extras.push(`<tr><td class="rb-it-sub" colspan="${ncols}">📝 ${esc(it.note)}</td></tr>`);
    }
    // One <tbody> per item, so a page break can't land between an item's
    // amounts row and its code / barcode / modifier / note rows. Multiple
    // tbodys in one table is valid HTML and doesn't affect column widths.
    return `<tbody class="rb-it-group${it.isReturn ? ' rb-it-return' : ''}">${main}${extras.join('')}</tbody>`;
  };

  const sectionHead = (label: string): string =>
    `<tbody><tr><td class="rb-it-sec-head" colspan="${ncols}">${esc(label)}</td></tr></tbody>`;

  // Return/exchange: split the lines into an Invoice (sold) section and a
  // Refund (returned) section, each under its own heading + entry number —
  // the same grouping the built-in receipt prints. Without this, a custom
  // design listed the returned items inline among the sold ones and only the
  // negative amounts hinted at which was which.
  const returned = model.items.filter((it) => it.isReturn);
  let body: string;
  if (model.exchange && returned.length > 0) {
    const ex = model.exchange;
    const sold = model.items.filter((it) => !it.isReturn);
    const parts: string[] = [];
    if (sold.length > 0) {
      const no = ex.saleEntryNo ? ` #${ex.saleEntryNo}` : '';
      parts.push(sectionHead(model.arabicLabels ? `فاتورة${no}` : `Invoice${no} — فاتورة`), ...sold.map(renderItemGroup));
    }
    const rno = ex.returnEntryNo ? ` #${ex.returnEntryNo}` : '';
    parts.push(sectionHead(model.arabicLabels ? `مرتجع${rno}` : `Refund${rno} — مرتجع`), ...returned.map(renderItemGroup));
    body = parts.join('');
  } else {
    body = model.items.map(renderItemGroup).join('');
  }

  return `<table class="rb-items"${styleAttr}><colgroup>${colgroup}</colgroup>${head}${body}</table>`;
}

function renderTotals(block: TotalsBlock, model: ReceiptRenderModel, css: string): string {
  // Return/exchange: the designed subtotal/discount/tax/total rows describe a
  // plain sale and are meaningless once both legs are on the ticket. Print the
  // two legs and the net settled instead — same as the built-in receipt.
  if (model.exchange) {
    const ex = model.exchange;
    // Keep whatever emphasis the design gave its TOTAL row for the NET row.
    const totalRow = block.rows.find((r) => r.key === 'total');
    const netCss = [
      totalRow?.bold === false ? '' : 'font-weight:700',
      totalRow?.fontSizePx ? `font-size:${totalRow.fontSizePx}px` : '',
    ].filter(Boolean).join(';');
    const rows: string[] = [];
    if (ex.soldTotal) {
      rows.push(
        `<div class="rb-row"><span class="rb-row-l">${model.arabicLabels ? 'المبيعات' : 'Sold — مبيعات'}</span><span class="rb-row-r">${esc(ex.soldTotal)}</span></div>`,
      );
    }
    if (ex.returnedTotal) {
      rows.push(
        `<div class="rb-row"><span class="rb-row-l">${model.arabicLabels ? 'المرتجع' : 'Returned — مرتجع'}</span><span class="rb-row-r">-${esc(ex.returnedTotal)}</span></div>`,
      );
    }
    rows.push(
      `<div class="rb-row"${netCss ? ` style="${netCss}"` : ''}><span class="rb-row-l">${esc(ex.netLabel)}</span><span class="rb-row-r">${esc(ex.netValue)}</span></div>`,
    );
    return `<div class="rb-totals"${css ? ` style="${css}"` : ''}>${rows.join('')}</div>`;
  }

  const hideZero = block.hideZero !== false;
  const promos = model.promotions ?? [];
  // Named promotions print where the design put its Discount row, in that
  // row's style; a design with no Discount row gets them above its TOTAL, and
  // one with neither at the end. The Discount row itself then carries only the
  // unnamed rest, and disappears when the promotions explain all of it.
  const promoAnchor: TotalsKey | null = promos.length === 0
    ? null
    : block.rows.some((r) => r.key === 'discount')
      ? 'discount'
      : block.rows.some((r) => r.key === 'total') ? 'total' : null;
  const rowCssOf = (r: TotalsRow): string => [
    r.bold ? 'font-weight:700' : '',
    r.fontSizePx ? `font-size:${r.fontSizePx}px` : '',
  ].filter(Boolean).join(';');
  const promoRows = (style: string): string =>
    promos
      .map(
        (p) =>
          `<div class="rb-row"${style ? ` style="${style}"` : ''}>` +
          `<span class="rb-row-l">${esc(p.label)}</span>` +
          `<span class="rb-row-r">-${esc(p.amount)}</span></div>`,
      )
      .join('');
  const rows = block.rows
    .map((r) => {
      const value = model.totals[r.key];
      // Discount row: after the promotions, only the unnamed rest — and no
      // blank row when there is none, even on a design that shows zeros.
      const skipRow =
        (hideZero || (promos.length > 0 && r.key === 'discount')) && (value == null || value === '');
      const label = model.arabicLabels
        ? (r.labelAr?.trim()
          || (r.label ? arabicTemplate(r.label) : '')
          || DEFAULT_TOTALS_LABELS_AR[r.key])
        : (r.label ?? DEFAULT_TOTALS_LABELS[r.key]);
      const rowCss = rowCssOf(r);
      const own = skipRow
        ? ''
        : `<div class="rb-row"${rowCss ? ` style="${rowCss}"` : ''}>` +
          `<span class="rb-row-l">${esc(label)}</span>` +
          `<span class="rb-row-r">${esc(r.valuePrefix ?? '')}${esc(value ?? '')}</span></div>`;
      if (promoAnchor !== r.key) return own;
      // After the Discount row; before the TOTAL row, and never in its style.
      return promoAnchor === 'discount' ? own + promoRows(rowCss) : promoRows('') + own;
    })
    .filter(Boolean)
    .join('');
  // "Show price inclusive of tax" (ShowItemAmountWithTax): the same note the
  // built-in slip prints under its totals, and on the same condition.
  const inclTax = model.printOptions?.showPriceWithTax === true && model.totals.tax
    ? `<div class="rb-incl-tax" style="font-size:0.85em;text-align:center">${esc(model.arabicLabels ? PRICES_INCL_TAX_AR : PRICES_INCL_TAX)}</div>`
    : '';
  return `<div class="rb-totals"${css ? ` style="${css}"` : ''}>${rows}${promoAnchor == null ? promoRows('') : ''}${inclTax}</div>`;
}

/** The built-in receipt's own words (`pricesInclTax` in `receiptLabels()`). */
const PRICES_INCL_TAX = 'Prices include tax';
const PRICES_INCL_TAX_AR = 'الأسعار شاملة الضريبة';

function renderPayments(block: PaymentsBlock, model: ReceiptRenderModel, css: string): string {
  const lines = model.payments
    .map(
      (p) =>
        `<div class="rb-row"><span class="rb-row-l">${esc(p.label)}</span><span class="rb-row-r">${esc(p.amount)}</span></div>`,
    )
    .join('');
  const changeLabel = model.arabicLabels ? DEFAULT_TOTALS_LABELS_AR.change : DEFAULT_TOTALS_LABELS.change;
  const change =
    block.showChange !== false && model.changeValue
      ? `<div class="rb-row" style="font-weight:700"><span class="rb-row-l">${esc(changeLabel)}</span><span class="rb-row-r">${esc(model.changeValue)}</span></div>`
      : '';
  return `<div class="rb-payments"${css ? ` style="${css}"` : ''}>${lines}${change}</div>`;
}

const DEFAULT_FONT_STACK = 'Tahoma, "Geeza Pro", "Noto Sans Arabic", Arial, sans-serif';

/**
 * Render a full HTML document for a custom receipt design.
 *
 * Output mirrors the built-in receipt template: a complete <!DOCTYPE html>
 * doc with @page thermal sizing, meant to be written into a print iframe.
 */
/**
 * Imageable width for a given paper width, in millimetres.
 *
 * The two thermal standards at 203 dpi: an "80 mm" head images 576 dots
 * (72.0 mm), a "58 mm" head images 384 dots (48.0 mm). Anything else falls
 * back to 90% of the paper, which is the right order of magnitude for the
 * oddball sizes.
 */
export function defaultPrintWidthMm(paperWidthMm: number): number {
  if (paperWidthMm >= 70) return 72;
  if (paperWidthMm >= 50) return 48;
  return Math.round(paperWidthMm * 0.9);
}

/** Render-time overrides that are NOT part of the saved design. */
export interface ReceiptRenderOptions {
  /**
   * 'a4' lays the same blocks out on an A4 sheet instead of the thermal band —
   * used when the tenant has the PrintReceiptA4 POS setting on. The design's
   * own `page.widthMm` / `printWidthMm` are ignored in that mode (a 72 mm
   * ribbon centred on a sheet of A4 is not what the tenant asked for); its
   * fonts, colours, direction and block order still apply.
   */
  paper?: 'thermal' | 'a4';
}

export function renderReceiptDesignHtml(
  design: ReceiptDesign,
  model: ReceiptRenderModel,
  opts: ReceiptRenderOptions = {},
): string {
  const a4 = opts.paper === 'a4';
  const page = design.page ?? { widthMm: 80 };
  const widthMm = page.widthMm || 80;
  const marginMm = page.marginMm ?? 3;
  // Lay the receipt out on a page that IS the printable band (72 mm on an
  // 80 mm head), not on the full paper width with padding standing in for it.
  //
  // The padding approach kept clipping: it assumes the driver maps our 80 mm
  // page 1:1 onto 80 mm of paper starting at dot 0, so that content stopping
  // at 72 mm lands inside the band. Real drivers disagree — some inset the
  // band, some rolls measure 76 mm not 80 — and every millimetre of
  // disagreement eats the right-hand column, which is where the receipt
  // number, the date and the amounts live. Sizing the PAGE to the band leaves
  // the driver nothing to clip: whatever it does with the leftover paper, all
  // the content is already inside the dots. It is also what the server-side
  // raster path has always done (576 dots wide, see rasterCanvas.ts).
  const printWidthMm = page.printWidthMm ?? defaultPrintWidthMm(widthMm);
  const baseFontPx = page.baseFontPx ?? 12;
  const fontFamily = page.fontFamily || DEFAULT_FONT_STACK;
  const color = page.color || '#000';
  const dir = page.direction === 'auto' || !page.direction ? model.direction : page.direction;

  // Fill whatever paper the printer is loaded with — see ReceiptPage.fitPaper.
  //
  // The narrow branch is byte-for-byte today's geometry: the same band width,
  // the same base font, the same body padding. Only the @page rule differs,
  // because a forced page size is what makes the sheet a ribbon.
  //
  // The wide branch keeps the design at its band width and ZOOMS it, rather
  // than re-flowing it. A re-flow would stretch a three-column items table
  // across 186 mm and leave 12 px text swimming in white space — the thing
  // the tenant called "not filling the paper". Zoom is a true enlargement:
  // every proportion the slip was drawn with survives, and unlike `transform`
  // it affects layout, so a long receipt still paginates.
  //
  // 120 mm is the threshold because no thermal roll reaches it (the widest
  // common head images 72 mm) and every sheet size clears it (A5 is 148 mm).
  const fit = !a4 && page.fitPaper === true;
  const A4_SIDE_MM = 12;
  const fitScale = Math.max(1, (210 - 2 * A4_SIDE_MM) / printWidthMm);
  const fitCss = `
  @page { size: auto; margin: 0; }
  * { box-sizing: border-box; }
  html { background: #fff; }
  html, body { margin: 0; padding: 0; width: ${printWidthMm}mm; background: #fff; color: ${color};
    font-family: ${fontFamily}; font-size: ${baseFontPx}px; line-height: 1.35; }
  body { padding: ${marginMm}mm; }
  @media print and (min-width: 120mm) {
    /* The band is unchanged and the ZOOM does the filling: ${printWidthMm}mm
       x ${fitScale.toFixed(3)} = ${(printWidthMm * fitScale).toFixed(1)}mm of a 210mm sheet. */
    /* The root box is the SHEET here, not the band. Left at ${printWidthMm}mm it
       is narrower than the slip it contains, so the zoomed body simply spills
       out of it — which prints, but leaves no margin at all on the right-hand
       edge and nothing for the script below to centre the slip within. */
    html { width: auto; padding: ${A4_SIDE_MM}mm; }
    body { zoom: ${fitScale.toFixed(4)}; }
    /* A sheet IS paginated, so the items header becomes a real header group
       again and repeats — the opposite of what a continuous roll wants. */
    .rb-items thead { display: table-header-group; }
    /* The items already defend themselves row by row. These two are small
       enough to keep whole, so the one receipt long enough to still break
       across sheets does not tear its money in half at the cut. */
    .rb-totals, .rb-payments { break-inside: avoid; page-break-inside: avoid; }
  }`;

  // ONE sheet, however many items — the second half of `fitPaper`.
  //
  // The zoom above fills the sheet's WIDTH, and width is all CSS can reason
  // about: a media query cannot see how tall the content is. So a slip longer
  // than ~105 mm of roll — three items, on the design this was built for —
  // spilled onto a second sheet and a third. The header it reprints there is
  // the shop's stationery, not a continuation, and the tenant reads two sheets
  // as two receipts for one sale.
  //
  // So the page measures itself, and it has TWO levers, because scale alone
  // cannot do it. A receipt is a tall ribbon and a sheet is wide: the zoom
  // that makes a 283 mm ribbon fit the height is 0.875, which prints it as a
  // 63 mm strip down the middle of a 216 mm page (measured off a real print,
  // 2026-09-16). Fitting the height by shrinking always costs the width.
  //
  // The second lever is the BAND ITSELF. Lay the same slip out on a wider
  // paper and its lines stop wrapping — two-line item names become one — so
  // the ribbon gets shorter as it gets wider, and the zoom that fills the
  // sheet's width can then also fit its height. That is what actually fills a
  // sheet with ONE column of items under each other, which is the document
  // the shop hands over; side-by-side columns keep the print bigger still and
  // were rejected for exactly that reason.
  //
  // So: measure the slip in a hidden copy of its own body at its own band,
  // then at wider and wider bands, and take the FIRST that fits a page at the
  // width-filling zoom. First, not best — the narrowest band that fits is the
  // one with the largest print. A short receipt fits at its own band on the
  // first try and is therefore byte-for-byte the sheet it prints today; the
  // band only widens where the alternative was a strip or a second page.
  //
  // Everything is written into the wide-paper media query, so the roll never
  // sees any of it, and a browser where the script does not run gets exactly
  // today's behaviour.
  //
  // Four numbers that are judgement, not geometry:
  //  - **US Letter, not A4**, as the sheet to budget for: it is the SHORTER
  //    of the two papers a wide print can land on (279 mm against 297 mm) and
  //    the width-filling zoom already sizes itself to the narrower. Budgeting
  //    for the smaller sheet in both directions is what makes "one page" true
  //    whichever is loaded; the price is 18 mm of white at the foot of an A4.
  //  - **3% is held back.** Zoom is applied to layout, so line boxes and glyph
  //    metrics re-round at the zoomed size and the printed height comes within
  //    a whisker of the measured one, not equal to it. A whisker over is a
  //    blank second page.
  //  - **The band widens a quarter of itself at a time, to five times its own
  //    width.** At 5x the width-filling zoom is 0.52, so the print is half the
  //    size it is drawn at — past that a wider band is not buying legibility
  //    any more, it is spending it.
  //  - **0.25 is the floor** for the shrink that is left when even the widest
  //    band does not fit — ~1 m of roll, far past any real order, and there so
  //    that a runaway receipt degrades into two pages instead of into a grey
  //    smear no one can read.
  const FIT_SHEET_MM = 279;
  const FIT_SAFETY = 0.97;
  const FIT_FLOOR = 0.25;
  /** How far the band may be widened, and in what steps, as multiples of itself. */
  const FIT_MAX_BAND = 5;
  const FIT_BAND_STEP = 0.25;
  const fitSlotId = 'rb-fit-onepage';
  const fitScript = `<script>(function(){
  var slot = document.getElementById(${JSON.stringify(fitSlotId)});
  if (!slot) return;
  var BAND = ${printWidthMm}, PAD = ${marginMm}, SIDE = ${A4_SIDE_MM};
  var SHEET_W = ${210 - 2 * A4_SIDE_MM}, SHEET_H = ${((FIT_SHEET_MM - 2 * A4_SIDE_MM) * FIT_SAFETY).toFixed(2)};
  var MAX_BAND = BAND * ${FIT_MAX_BAND}, STEP = BAND * ${FIT_BAND_STEP};
  var FLOOR = ${FIT_FLOOR}, MM = 25.4 / 96;
  var last = '';

  /* Measure the slip at a given band width, in millimetres, in a hidden COPY
     of the body: re-laying the real one out to measure it would flash the
     rearranged receipt at whoever is standing at the counter. The copy carries
     the body's own padding, and inherits everything else from it. */
  function measurer() {
    var body = document.body;
    if (!body) return null;
    var box = document.createElement('div');
    box.setAttribute('aria-hidden', 'true');
    box.style.cssText = 'position:absolute;left:-10000px;top:0;visibility:hidden;padding:' + PAD + 'mm;';
    box.innerHTML = body.innerHTML;
    body.appendChild(box);
    /* Pin the copy's images to the size the real ones already render at: a
       copy re-requests them, and an image still deciding its size measures 0,
       which would buy a second page. */
    var srcImgs = body.getElementsByTagName('img'), boxImgs = box.getElementsByTagName('img');
    for (var i = 0; i < boxImgs.length && i < srcImgs.length; i++) {
      var iw = srcImgs[i].offsetWidth, ih = srcImgs[i].offsetHeight;
      if (iw > 0 && ih > 0) { boxImgs[i].style.width = iw + 'px'; boxImgs[i].style.height = ih + 'px'; }
    }
    return {
      at: function (w) { box.style.width = w + 'mm'; return box.offsetHeight * MM; },
      done: function () { body.removeChild(box); }
    };
  }

  /* The narrowest band whose slip fits one page at the zoom that fills the
     sheet's width — which is the band with the largest print. */
  function plan() {
    var m = measurer();
    if (!m) return null;
    try {
      var widest = null;
      for (var w = BAND; w <= MAX_BAND + 0.001; w += STEP) {
        var h = m.at(w);
        if (!(h > 0)) return null;
        var z = SHEET_W / w;
        if (h * z <= SHEET_H) return { w: w, z: z };
        widest = { w: w, h: h };
      }
      /* Not even the widest band fits: keep it and shrink what is left. */
      return { w: widest.w, z: Math.max(Math.min(SHEET_W / widest.w, SHEET_H / widest.h), FLOOR) };
    } finally {
      m.done();
    }
  }

  function fit() {
    var p = plan();
    if (!p) return;
    /* Centre whatever is left over, as an explicit page padding. 'auto'
       margins cannot do this: asked to centre a zoomed body Chrome resolves
       the left one to 0 and the right one to a negative number, and the slip
       stays hard against the left edge — which reads as a fault, not a fit. */
    var side = SIDE + Math.max(0, (SHEET_W - p.w * p.z) / 2);
    var css = '@media print and (min-width: 120mm){' +
      'html{padding-inline:' + side.toFixed(2) + 'mm;}' +
      'body{zoom:' + p.z.toFixed(4) + ';' +
      (p.w > BAND ? 'width:' + p.w.toFixed(2) + 'mm;' : '') +
      '}}';
    if (css === last) return;
    last = css;
    slot.textContent = css;
  }

  fit();
  /* The logo and the font decide the height as much as the items do, and
     neither has arrived at parse time. 'beforeprint' is the last word: it
     fires while the document is still in screen layout, so the measurement
     above is still the right one to take. */
  window.addEventListener('load', fit);
  window.addEventListener('beforeprint', fit);
})();<\/script>`;

  // A4 mode (PrintReceiptA4): a real sheet with printer margins, the content
  // filling its width, and a slightly larger base font (a 12 px thermal body
  // reads as fine print on paper three times as wide).
  const pageCss = a4
    ? `
  @page { size: A4; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; width: 100%; background: #fff; color: ${color};
    font-family: ${fontFamily}; font-size: ${Math.max(baseFontPx, 13)}px; line-height: 1.45; }`
    : fit
    ? fitCss
    : `
  @page { size: ${printWidthMm}mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; width: ${printWidthMm}mm; background: #fff; color: ${color};
    font-family: ${fontFamily}; font-size: ${baseFontPx}px; line-height: 1.35; }
  body { padding: ${marginMm}mm; }`;

  const css = `${pageCss}
  /* A float left hanging at the end of the document does not contribute to the
     body's height, and an auto-height page then measures a roll shorter than
     what is on it — the tail gets cut. Clearing after the last block costs
     nothing on a design that floats nothing. */
  body::after { content: ''; display: block; clear: both; }
  .rb-text { white-space: pre-wrap; word-break: break-word; }
  .rb-row { display: flex; justify-content: space-between; align-items: baseline; gap: 6px; }
  /* The label must be able to shrink AND wrap: with a rigid label and a
     nowrap value (a long receipt number, a full timestamp), the pair
     overflowed the paper and the printer ate the value's tail — "#20" for
     "#2014", "04:34 P" for "04:34 PM". min-width:0 defeats the flex default
     of min-width:auto so both cells can actually shrink. */
  .rb-row-l { flex: 0 1 auto; min-width: 0; overflow-wrap: anywhere; }
  .rb-row-r { flex: 1 1 auto; min-width: 0; text-align: end; unicode-bidi: plaintext; }
  .rb-divider { margin: 4px 0; }
  .rb-img img { display: inline-block; }
  /* A floated block is out of the flow, so give it a little breathing room on
     the side the text wraps past and stop the bars touching the paper edge. */
  .rb-img[style*="float:left"], .rb-barcode[style*="float:left"] { margin-right: 6px; }
  .rb-img[style*="float:right"], .rb-barcode[style*="float:right"] { margin-left: 6px; }
  /* The SVG is sized in px by the encoder; never let it outgrow the band. */
  .rb-barcode svg { display: inline-block; max-width: 100%; height: auto; }
  /* The items table is where a receipt stops being a header, so it always
     starts below anything floated above it — no design should have to say so. */
  .rb-items { width: 100%; border-collapse: collapse; clear: both; }
  /* A receipt is ONE continuous ticket, not a paginated document. A table head
     is a "header group", which every print engine faithfully REPEATS at the top
     of each new page — so a long order printed the Item/Qty/Price/Total bar
     again in the middle of the roll, once per page break. Demoting it to an
     ordinary row group prints it once, where it sits in the markup, while
     keeping it inside the table so it still shares the column widths.
     On A4 the opposite is right: it IS a paginated document, so the head stays
     a real header group and repeats on every sheet. */
  ${a4 ? '' : '.rb-items thead { display: table-row-group; }'}
  .rb-items th { font-weight: 700; padding: 1px 2px; border-bottom: 1px solid currentColor; }
  /* Keep an item whole across a page break — its names, its amounts and its
     code/modifier/note sub-rows (one row group per item) stay together instead
     of being torn in half at the cut. */
  .rb-items tr { break-inside: avoid; page-break-inside: avoid; }
  .rb-it-group { break-inside: avoid; page-break-inside: avoid; }
  .rb-it-cell { padding: 2px 2px; vertical-align: top; }
  .rb-it-name { font-weight: 600; word-break: break-word; }
  .rb-it-ar { font-weight: 600; }
  .rb-it-sub { padding: 0 2px 2px; color: #444; font-size: 0.9em; }
  /* Invoice / Refund section headings on a return-or-exchange receipt. Returned
     lines are identified by this heading, not by struck-through text (per ops). */
  .rb-it-sec-head {
    padding: 3px 2px;
    margin-top: 4px;
    border-top: 1px dashed currentColor;
    border-bottom: 1px dashed currentColor;
    font-weight: 700;
    text-align: center;
  }
  .rb-totals .rb-row { padding: 1px 0; }
  .rb-payments .rb-row { padding: 1px 0; }
  .footer-html { margin-top: 4px; font-size: 10px; }
  .footer-html * { max-width: 100%; }
  `;

  const blocks = design.blocks ?? [];
  // A ZATCA tenant's receipt must carry the QR whether or not its design was
  // drawn with the block: a design without one gets it appended here, at the
  // end. `zatcaQrSvg` is only ever set for a tenant set up for ZATCA, so every
  // other design renders exactly as before.
  const autoQr = model.zatcaQrSvg && !blocks.some((b) => b.type === 'zatca_qr')
    ? `\n<div class="rb-divider" style="border-top:1px dashed currentColor"></div>\n${renderBlock({ id: 'auto_zatca_qr', type: 'zatca_qr' }, model)}`
    : '';
  const body = blocks.map((b) => renderBlock(b, model)).join('\n') + autoQr;
  const footer = model.footerHtml
    ? `<div class="rb-divider" style="border-top:1px dashed currentColor"></div><div class="footer-html">${model.footerHtml}</div>`
    : '';

  return `<!DOCTYPE html>
<html lang="${dir === 'rtl' ? 'ar' : 'en'}" dir="${dir}">
<head>
<meta charset="utf-8">
<title>Receipt</title>
<style>${css}</style>${
  // Empty, and LAST in the head so the rule the script writes into it beats
  // the width-filling zoom above on equal specificity.
  fit ? `\n<style id="${fitSlotId}"></style>` : ''
}
</head>
<body>
${body}
${footer}${fit ? `\n${fitScript}` : ''}
</body>
</html>`;
}
